// CompliancePage — Phase 1 cleanup — cross-module compliance dashboard.
import { useEffect, useState } from "react";
import { Loader2, ShieldAlert, AlertTriangle, MessageSquare, FileX } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useView } from "@/context/ViewContext";
import { useSortableRows, SortableTh } from "@/lib/sortable";

function scoreColour(s) {
    if (s >= 80) return "#3F7D3A";
    if (s >= 60) return "#C68A1C";
    return "#B43A3A";
}

function ScoreBar({ score }) {
    return (
        <div className="w-full h-1.5 bg-[#F0EDE8] rounded overflow-hidden">
            <div className="h-full transition-all" style={{ width: `${score}%`, background: scoreColour(score) }} />
        </div>
    );
}

export default function CompliancePage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const { portalClientId, isInternalView } = useView();

    useEffect(() => {
        const params = {};
        if (portalClientId && !isInternalView) params.client_id = portalClientId;
        api.get("/compliance", { params }).then((r) => setData(r.data))
            .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Cannot load compliance"))
            .finally(() => setLoading(false));
    }, [portalClientId, isInternalView]);

    if (loading) return <div className="text-center py-10 text-[#9A968F]"><Loader2 size={16} className="inline animate-spin mr-2" />Loading…</div>;
    if (!data) return null;
    return <CompliancePageInner data={data} />;
}

function CompliancePageInner({ data }) {
    const { sortKey, sortDir, toggleSort, sorted } = useSortableRows(data.clients, {
        defaultKey: "score", defaultDir: "asc",
        accessors: {
            client: (r) => r.client.name || "",
            score: (r) => r.metrics.compliance_score,
            buildings: (r) => r.metrics.buildings,
            actions: (r) => r.metrics.open_actions,
            overdue: (r) => r.metrics.overdue_actions,
            inspections: (r) => r.metrics.inspections_total,
            docs: (r) => r.metrics.docs_approved,
            riddor: (r) => r.metrics.riddor_open,
            concerns: (r) => r.metrics.concerns_open,
        },
    });

    return (
        <div data-testid="compliance-page" className="space-y-5">
            <div>
                <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                    style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>Safety &amp; Compliance</span>
                <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">Compliance Tracker</h2>
                <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                    Cross-module compliance snapshot per client — score blends action close-out, document approval, inspection completion &amp; portal engagement (logins), penalising overdue / RIDDOR / expired.
                </p>
            </div>

            <div className="grid grid-cols-5 gap-3" data-testid="compliance-rollup">
                <RollupCard label="Avg compliance score" value={`${data.rollup.avg_score}%`} colour={scoreColour(data.rollup.avg_score)} />
                <RollupCard label="Overdue actions" value={data.rollup.total_overdue_actions} colour="#C68A1C" icon={AlertTriangle} />
                <RollupCard label="RIDDOR open" value={data.rollup.total_riddor_open} colour="#B43A3A" icon={ShieldAlert} />
                <RollupCard label="Docs expired" value={data.rollup.total_docs_expired} colour="#B43A3A" icon={FileX} />
                <RollupCard label="Open concerns" value={data.rollup.total_concerns_open} colour="#C68A1C" icon={MessageSquare} />
            </div>

            <div className="rounded-2xl bg-white border border-[#E5E0DA] overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-[#F5F3F0] text-left border-b border-[#E5E0DA] group">
                        <tr>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="client" onClick={toggleSort}>Client</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="score" onClick={toggleSort} align="center">Score</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="buildings" onClick={toggleSort} align="center">Buildings</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="overdue" onClick={toggleSort} align="center">Actions (open · overdue)</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="inspections" onClick={toggleSort} align="center">Inspections</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="docs" onClick={toggleSort} align="center">Docs (approved · review · expired)</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="riddor" onClick={toggleSort} align="center">RIDDOR</SortableTh>
                            <SortableTh sortKey={sortKey} sortDir={sortDir} myKey="concerns" onClick={toggleSort} align="center">Concerns</SortableTh>
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map(({ client, metrics }) => (
                            <tr key={client.id} data-testid={`compliance-row-${client.id}`}
                                className="border-b border-[#F0EDE8] hover:bg-[#F5F3F0]">
                                <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                        <span className="w-2 h-8 rounded" style={{ background: client.branding?.primary_colour || "#9A968F" }} />
                                        <div>
                                            <div className="font-semibold text-[#1F1F1F]">{client.name}</div>
                                            <div className="text-[11px] text-[#9A968F]">{client.status} · {client.risk} risk</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-4 py-3 text-center">
                                    <div className="font-bold text-lg" style={{ color: scoreColour(metrics.compliance_score) }}
                                        data-testid={`compliance-score-${client.id}`}>{metrics.compliance_score}</div>
                                    <ScoreBar score={metrics.compliance_score} />
                                </td>
                                <td className="px-4 py-3 text-center text-[#6F6A64]">
                                    {metrics.buildings}
                                    {metrics.high_risk_buildings > 0 && (
                                        <div className="text-[11px] text-red-600">{metrics.high_risk_buildings} high-risk</div>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-center text-[#6F6A64]">
                                    {metrics.open_actions} ·{" "}
                                    <span className={metrics.overdue_actions > 0 ? "text-red-600 font-semibold" : ""}>{metrics.overdue_actions}</span>
                                    <div className="text-[11px] text-[#9A968F]">{metrics.close_out_rate}% closed</div>
                                </td>
                                <td className="px-4 py-3 text-center text-[#6F6A64]">
                                    {metrics.inspections_submitted}/{metrics.inspections_total}
                                    {metrics.inspections_overdue > 0 && (
                                        <div className="text-[11px] text-red-600">{metrics.inspections_overdue} overdue</div>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-center text-[#6F6A64] text-[12px]">
                                    <span className="text-[#3F7D3A] font-semibold">{metrics.docs_approved}</span> · {metrics.docs_review} ·{" "}
                                    <span className={metrics.docs_expired > 0 ? "text-red-600 font-semibold" : ""}>{metrics.docs_expired}</span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                    {metrics.riddor_open > 0 ? (
                                        <span className="inline-flex items-center gap-1 text-red-600 font-semibold"><ShieldAlert size={12} />{metrics.riddor_open}</span>
                                    ) : <span className="text-[#9A968F]">—</span>}
                                </td>
                                <td className="px-4 py-3 text-center text-[#6F6A64]">{metrics.concerns_open || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function RollupCard({ label, value, colour, icon: Icon }) {
    return (
        <div className="rounded-2xl bg-white border border-[#E5E0DA] p-4">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#9A968F] font-bold">
                {Icon && <Icon size={11} />}{label}
            </div>
            <div className="mt-1 text-2xl font-bold" style={{ color: colour }}>{value}</div>
        </div>
    );
}
